import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND } from "../lib/brand";
import { Toaster } from "./ui/sonner";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/clarity-kit", label: "Clarity Kit" },
  { to: "/clarity-diagnostic", label: "Free Clarity Check" },
  { to: "/insights", label: "Insights" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-bos-white/85 backdrop-blur-md border-b border-bos-ice"
    >
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-5 flex items-center justify-between">
        <Link to="/" data-testid="logo-home-link" className="flex items-center gap-3">
          <img
            src={BRAND.logoUrl}
            alt="BoS"
            className="h-9 w-9 object-contain"
            data-testid="logo-mark"
          />
          <span className="leading-none">
            <span className="font-semibold text-bos-navy text-[19px] tracking-tight">
              BuildOnce
            </span>
            <span className="text-bos-charcoal text-[19px] ml-1.5 tracking-[0.14em]">
              Studio
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[14px] tracking-tight transition-colors ${
                  isActive ? "text-bos-navy" : "text-bos-charcoal hover:text-bos-primary"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/clarity-diagnostic"
          data-testid="header-cta-book"
          className="hidden lg:inline-flex items-center px-5 py-2.5 text-[14px] text-bos-white bg-bos-navy hover:bg-bos-primary transition-colors rounded-full"
        >
          Try the Free Clarity Check
        </Link>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-bos-navy"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-bos-ice bg-bos-white" data-testid="mobile-nav">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[16px] ${isActive ? "text-bos-navy" : "text-bos-charcoal"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/clarity-diagnostic"
              data-testid="mobile-cta-book"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 text-[14px] text-bos-white bg-bos-navy rounded-full"
            >
              Try the Free Clarity Check
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="mt-32 border-t border-bos-ice bg-bos-white"
  >
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 grid gap-12 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr]">
      <div>
        <div className="flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="BoS" className="h-8 w-8 object-contain" />
          <span className="leading-none">
            <span className="font-semibold text-bos-navy text-[18px]">BuildOnce</span>
            <span className="text-bos-charcoal text-[18px] ml-1.5 tracking-[0.14em]">Studio</span>
          </span>
        </div>
        <p className="mt-5 max-w-sm text-[14px] text-bos-charcoal/80">
          Founder-led strategy and positioning for SMEs in Singapore and
          across Southeast Asia.
        </p>
        <p className="mt-4 text-[13px] text-bos-charcoal/60">
          {BRAND.tagline}
        </p>
        <p className="mt-3 text-[12px] text-bos-charcoal/55 tracking-[0.04em]">
          AI-supported. Human-led. Built for clearer business decisions.
        </p>
      </div>
      <div>
        <p className="bos-eyebrow mb-4">Studio</p>
        <ul className="space-y-2 text-[14px]">
          <li><Link to="/about" data-testid="footer-link-about">About</Link></li>
          <li><Link to="/services" data-testid="footer-link-services">Services</Link></li>
          <li><Link to="/case-studies/illustrative-clarity-sprint" data-testid="footer-link-case-study">Case study</Link></li>
          <li><Link to="/insights" data-testid="footer-link-insights">Insights</Link></li>
        </ul>
      </div>
      <div>
        <p className="bos-eyebrow mb-4">Engage</p>
        <ul className="space-y-2 text-[14px]">
          <li><Link to="/clarity-kit" data-testid="footer-link-clarity-kit">Clarity Kit</Link></li>
          <li><Link to="/clarity-diagnostic" data-testid="footer-link-ai-demo">Free Clarity Check</Link></li>
          <li><Link to="/contact" data-testid="footer-link-contact">Contact</Link></li>
        </ul>
      </div>
      <div>
        <p className="bos-eyebrow mb-4">Legal</p>
        <ul className="space-y-2 text-[14px]">
          <li><Link to="/privacy-policy" data-testid="footer-link-privacy">Privacy Policy</Link></li>
          <li><Link to="/terms-and-conditions" data-testid="footer-link-terms">Terms and Conditions</Link></li>
          <li><Link to="/refund-policy" data-testid="footer-link-refund">Refund Policy</Link></li>
          <li><Link to="/disclaimer" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
        </ul>
      </div>
      <div>
        <p className="bos-eyebrow mb-4">Reach us</p>
        <a
          href={`mailto:${BRAND.email}`}
          data-testid="footer-email"
          className="text-[14px] bos-link-underline"
        >
          {BRAND.email}
        </a>
      </div>
    </div>
    <div className="border-t border-bos-ice">
      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-6 flex items-center justify-between text-[12px] text-bos-charcoal/60">
        <span>© {new Date().getFullYear()} BuildOnce Studio</span>
        <span className="tracking-[0.12em] uppercase">Built once · Built right</span>
      </div>
    </div>
  </footer>
);

export default function Layout() {
  return (
    <div className="App bos-grain min-h-screen">
      <Header />
      <main data-testid="site-main">
        <Outlet />
      </main>
      <Footer />
      <Toaster richColors position="top-right" />
    </div>
  );
}
